const HERO_IMAGE =
  'https://images.unsplash.com/photo-1571068316344-75bc76f77890?auto=format&fit=crop&w=1600&q=80';

/**
 * Featured guide: mountain biking trails near Powell, Dublin, Polaris corridor,
 * Lewis Center, and Delaware, Ohio.
 */
export const OHIO_MOUNTAIN_BIKING_TRAILS_POST = {
  id: 'featured-ohio-mountain-biking-trails',
  slug: 'mountain-biking-trails-powell-dublin-polaris-delaware-ohio',
  title: 'Best Mountain Biking Trails Near Powell, Dublin, Polaris & Delaware (Ohio)',
  excerpt:
    'Where to ride singletrack without driving to Hocking Hills—Alum Creek’s COMBO-built network, Quarry Trails’ short downhill line, beginner skills parks, and paved river paths for family rides from Powell to Delaware.',
  publishedAt: '2026-07-24T23:10:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Trail Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '4 areas', label: 'Powell · Dublin · Polaris · Delaware' },
        { value: '20+ mi', label: 'Alum Creek singletrack' },
        { value: 'COMBO', label: 'Volunteer-built & maintained' },
        { value: 'Check mud', label: 'Respect trail closures' },
      ],
    },
    {
      type: 'panel',
      title: 'How to read this guide',
      paragraphs: [
        'Northern Delaware County and northwest Franklin County do not have dozens of lift-served bike parks—but they do have one of Central Ohio’s best trail networks at Alum Creek State Park, plus a few shorter options worth knowing.',
        '“Mountain biking” here means dirt singletrack with roots, bridges, and flow features—not paved multi-use paths. We still list a couple of paved river trails under each area for families warming up on hybrids or gravel bikes.',
        'Most trails are maintained by COMBO (Central Ohio Mountain Biking Organization). Check trail conditions before you drive—Ohio clay stays muddy for days after rain. Never ride closed or wet trails; it damages tread and gets trails shut down.',
      ],
      chips: ['Singletrack', 'Beginner to advanced', 'Ohio 43015–43065', 'Updated July 2026'],
    },
    {
      type: 'table',
      title: 'Powell & Lewis Center — Alum Creek State Park (the main destination)',
      intro:
        'This is the trail system Powell and Lewis Center riders reach first. Park at the Phase 1 lot on Lewis Center Road for beginner loops, Gnomewood skills park, and the ~6.5-mile Phase 1 intermediate loop. Phase 2 adds technical bridges and log piles on the east side of Africa Road (~15 minutes from Powell).',
      headers: ['Trail / area', 'Trailhead / address', 'Miles & level', 'Notes'],
      rows: [
        [
          'Gnomewood Skills Park + Beginner Loop',
          '6914 Lewis Center Rd, Galena, OH 43021 (Phase 1 lot)',
          '~2.5 mi combined · Easy',
          'Best first-timer spot in the region—small jumps, berms, rollers, and a mellow loop across Lewis Center Rd. COMBO’s Beginner Experience route starts here. Cross the road carefully.',
        ],
        [
          'Coaster Loop',
          'Same Phase 1 parking lot (west side, behind storage containers)',
          '0.3 mi · Easy',
          'Tiny wooded warm-up loop with optional roller features—great for kids before tackling Phase 1.',
        ],
        [
          'Alum Creek Phase 1 (P1)',
          '6914 Lewis Center Rd, Galena, OH 43021',
          '~6.5 mi · Intermediate',
          'Flowing cross-country singletrack along the reservoir with roots and ladder bridges. Directional: counter-clockwise Mon/Wed/Fri/Sat; clockwise Tue/Thu/Sun. Short Africa Rd connector (~1/8 mi).',
        ],
        [
          'Alum Creek Phase 2 (P2)',
          'Africa Rd lot north of Cheshire Rd, Lewis Center, OH 43035',
          '~6.5 mi · Difficult',
          'More ladder bridges, log piles, and tight tech than P1. Counter-clockwise. Separate parking on west side of Africa Rd; trail starts across the road.',
        ],
        [
          'P1 ↔ P2 Connector + full system',
          'Either Phase 1 or Phase 2 lot',
          'Up to ~25 mi combined · Intermediate+',
          'Link both phases for a long day. COMBO is expanding sustainable corridors toward a 26-mile network—watch for construction detours and closure signs.',
        ],
        [
          'Highbanks Metro Park (not MTB)',
          '9466 Columbus Pike (US 23 N), Lewis Center, OH 43035',
          '~2.7 mi paved · Easy',
          'Powell’s backyard park—paved Big Meadows and Multiuse paths only. Fine for kids on hybrids; not singletrack. Good cool-down spin after Alum Creek.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Polaris & I-71 corridor',
      intro:
        'There is no dedicated mountain bike park inside the Polaris retail district. From Polaris Fashion Place, Alum Creek Phase 1 is the closest real singletrack (~12–15 minutes north). Quarry Trails is a shorter option southwest toward Hilliard/Dublin Road.',
      headers: ['Trail / area', 'Trailhead / address', 'Miles & level', 'Notes'],
      rows: [
        [
          'Alum Creek (from Polaris north)',
          '6914 Lewis Center Rd, Galena, OH 43021',
          'See Powell section',
          'Default recommendation for Polaris-area riders—ample parking, beginner zone, and full Phase 1/2 system.',
        ],
        [
          'Quarry Trails Metro Park — MTB loop',
          '2600 Dublin Rd, Columbus, OH 43228 (Northern Area entrance)',
          '0.75 mi · Moderate',
          'Short but rugged Metro Parks singletrack with a visible downhill line from the Observation Trail overlook. Best for a quick session or skills work—not a full afternoon ride.',
        ],
        [
          'Quarry Trails — paved multi-use',
          '2158 Old Dublin Rd or 2319 Quarry Trails Dr, Columbus, OH 43228',
          'Varies · Easy',
          'Scenic quarry lakes, waterfall overlook, and paved paths—hybrid/gravel friendly. Via ferrata and sport climbing on site; MTB loop is separate entrance on Dublin Rd.',
        ],
        [
          'Olentangy Trail (paved, not MTB)',
          'Antrim Park, 5800 Olentangy River Rd, Columbus, OH 43085',
          '~21 mi total · Easy',
          'Closest paved river path to Polaris—great for road/gravel bikes and family rides along the Olentangy. Not singletrack; connects south toward OSU and north toward Worthington.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Dublin — Quarry Trails & west-side access',
      intro:
        'Dublin riders often split time between Quarry Trails’ short MTB loop and the drive north to Alum Creek. Glacier Ridge and most Dublin-adjacent Metro Parks are paved multi-use only—do not expect dirt jumps or berms there.',
      headers: ['Trail / area', 'Trailhead / address', 'Miles & level', 'Notes'],
      rows: [
        [
          'Quarry Trails MTB trail',
          '2600 Dublin Rd, Columbus, OH 43228',
          '0.75 mi · Moderate',
          '~15–20 min from Dublin via I-270/SR 161. Rugged, rocky singletrack in a former quarry—pair with the observation overlook to watch riders on the descent.',
        ],
        [
          'Alum Creek Phase 1 (from Dublin)',
          '6914 Lewis Center Rd, Galena, OH 43021',
          '~6.5 mi+ · Intermediate',
          '~20–25 min northeast via US 23 and Lewis Center Rd. Still the best full-length option for Dublin riders who want flow and mileage.',
        ],
        [
          'Glacier Ridge Metro Park (not MTB)',
          '9801 Hyland Croy Rd, Plain City, OH 43064',
          'Paved loops · Easy',
          'Ironweed and Marsh Hawk paved paths—excellent for kids and gravel bikes, not mountain biking. Reserve expectations: no berms or technical dirt.',
        ],
        [
          'Olentangy Trail — Dublin / SR 161 access',
          'Multiple access points along SR 161 and Riverside Dr',
          'Paved · Easy',
          'Useful connector for road and gravel riders; crosses under SR 161 near Tuttle and runs through Antrim and Worthington. Not a substitute for Alum Creek singletrack.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Delaware — closest singletrack & river paths',
      intro:
        'Delaware city riders have two realistic options: drive south to Alum Creek for real mountain biking, or stay local on the Olentangy Trail and Delaware’s paved path network. Delaware State Park does not offer dedicated MTB singletrack.',
      headers: ['Trail / area', 'Trailhead / address', 'Miles & level', 'Notes'],
      rows: [
        [
          'Alum Creek Phase 1 & 2 (from Delaware)',
          '6914 Lewis Center Rd or Africa Rd lot, Lewis Center area',
          'See Powell section',
          '~15–20 min south on US 23—closest and best singletrack for Delaware County riders north of Columbus.',
        ],
        [
          'Olentangy Trail — Delaware end',
          'Mingo Park, 500 E Lincoln Ave, Delaware, OH 43015',
          'Paved · Easy',
          'Northern terminus of the Olentangy multi-use path—flat, scenic river riding into Columbus. Good for gravel bikes and fitness spins; not technical MTB.',
        ],
        [
          'Delaware city paved paths',
          'Downtown Delaware & Mingo Park connectors',
          'Varies · Easy',
          'Link neighborhoods and the river corridor on asphalt—family friendly. Combine with Mingo Park as a starting point for longer paved rides south.',
        ],
        [
          'Chestnut Ridge Metro Park (day trip)',
          '5260 Mason Rd, Canal Winchester, OH 43110',
          '~11 mi MTB · Intermediate–expert',
          '~40–45 min from Delaware—COMBO-built stacked loops with pump track and “More Cowbell” gravity line. Mountain biking only on the dirt system; separate from hiking entrances.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Suggested ride plans by experience',
      list: [
        'First time on dirt: Gnomewood Skills Park → Coaster Loop → Beginner Loop (Alum Creek Phase 1 lot). Allow 45–60 minutes.',
        'Intermediate Saturday: Phase 1 full loop (~6.5 mi). Check directional day before you start.',
        'Looking for tech: Phase 2 or link P1 + connector + P2 for a long day (~20+ mi with road connectors).',
        'Quick weekday hit from Polaris/Dublin: Quarry Trails MTB loop (0.75 mi) repeated laps or skills session.',
        'Family with young kids: Highbanks paved paths or Olentangy Trail from Antrim—hybrids OK, no suspension required.',
      ],
    },
    {
      type: 'panel',
      title: 'Trail etiquette & mud rules',
      orderedList: [
        'Check COMBO trail conditions (combomtb.com) or Metro Parks trail hotline before leaving home.',
        'If you leave ruts or your tires pick up wet clay, turn around—riding muddy trails gets systems closed.',
        'Yield to hikers only where trails are shared; Alum Creek MTB and Chestnut Ridge MTB loops are bike-only.',
        'Ride Phase 1 and Phase 2 in the posted direction for the day of the week.',
        'Pack water, spare tube, and mini-tool—cell service is spotty in the woods at Alum Creek.',
        'Helmets are non-negotiable; gloves and knee pads help on Phase 2 bridges and Gnomewood features.',
      ],
      callout:
        'Trail mileage, closures, and construction zones change seasonally. COMBO and Ohio State Parks post the latest maps—use the official links below before planning a new weekly ride group.',
    },
  ],
  relatedPostsTitle: 'More Ohio outdoor guides',
  relatedPosts: [
    {
      slug: 'alum-creek-best-hike-spots-beginner-moderate',
      title: 'Best Alum Creek Hike Spots (Beginner to Moderate Plan)',
      blurb: 'hiking-only lots and trails — not the COMBO bike loops.',
    },
    {
      slug: 'ohio-beginner-hiking-guide',
      title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
      blurb: 'hiking-only trails near the same parks—stay off the bike loops.',
    },
    {
      slug: 'ohio-summer-nature-kids-family-guide',
      title: 'Ohio Summer Nature Guide for Families (Ages 3–13)',
      blurb: 'waterfalls, beaches, and state-park days without bikes.',
    },
  ],
  referenceLinks: [
    {
      label: 'COMBO — trail conditions & Alum Creek project updates',
      url: 'https://www.combomtb.com/',
    },
    {
      label: 'COMBO — Beginner Experience at Alum Creek (Gnomewood & Coaster Loop)',
      url: 'https://www.combomtb.com/beginnerexp',
    },
    {
      label: 'Ohio DNR — Alum Creek mountain biking map (PDF)',
      url: 'https://dam.assets.ohio.gov/image/upload/ohiodnr.gov/documents/parks/parkmaps/alumcreekmountainbiketrail.pdf',
    },
    {
      label: 'Metro Parks — Quarry Trails (mountain bike trail)',
      url: 'https://www.metroparks.net/parks-and-trails/quarry-trails/',
    },
    {
      label: 'Metro Parks — Chestnut Ridge mountain bike trail',
      url: 'https://www.metroparks.net/parks-and-trails/chestnut-ridge/',
    },
    {
      label: 'Metro Parks — Highbanks (paved bike paths)',
      url: 'https://www.metroparks.net/parks-and-trails/highbanks/',
    },
    {
      label: 'TrailLink — Olentangy Trail (paved river path)',
      url: 'https://www.traillink.com/trail/olentangy-trail/',
    },
  ],
};
